import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { anunciosService, getFotoUrl } from '../services/api'
import Selo from '../components/Selo'
import AnuncioCard from '../components/AnuncioCard'
import './Anuncios.css'

const categorias = ['Todas', 'Bovino', 'Equino', 'Suíno', 'Ovino', 'Caprino', 'Outros']
const racas = ['Todas', 'Nelore', 'Angus', 'Brahman', 'Gir', 'Holandesa', 'Senepol', 'Outra']
const estados = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
]

function Anuncios() {
    // Estados dos Filtros
    const [filtroCategoria, setFiltroCategoria] = useState('Todas')
    const [filtroRaca, setFiltroRaca] = useState('Todas')
    const [filtroEstado, setFiltroEstado] = useState('')
    const [filtroCidade, setFiltroCidade] = useState('')
    const [precoMin, setPrecoMin] = useState('')
    const [precoMax, setPrecoMax] = useState('')
    const [ordenacao, setOrdenacao] = useState('selo')

    // Busca Textual
    const [busca, setBusca] = useState('')

    // URL Params
    const [searchParams] = useSearchParams()

    // Dados da página
    const [anuncios, setAnuncios] = useState([])
    const [loading, setLoading] = useState(true)
    const [totalAnuncios, setTotalAnuncios] = useState(0)

    // Contextos
    const { usuario, isLogado } = useAuth()
    const navigate = useNavigate()

    // Filtros mobile
    const [showFilters, setShowFilters] = useState(false)

    // Carregar busca e categoria da URL na montagem
    useEffect(() => {
        const queryBusca = searchParams.get('busca')
        if (queryBusca) {
            setBusca(queryBusca)
        }
        const queryCategoria = searchParams.get('categoria')
        if (queryCategoria) {
            setFiltroCategoria(queryCategoria)
        }
    }, [searchParams])

    useEffect(() => {
        carregarAnuncios()
    }, [filtroCategoria, filtroRaca, filtroEstado, ordenacao]) // Recarrega ao mudar esses filtros diretos

    const carregarAnuncios = async () => {
        setLoading(true)
        try {
            const filtros = {
                ordenar: ordenacao,
                ...(filtroCategoria !== 'Todas' && { categoria: filtroCategoria }),
                ...(filtroRaca !== 'Todas' && { raca: filtroRaca }),
                ...(filtroEstado && { estado: filtroEstado }),
                ...(filtroCidade && { cidade: filtroCidade }),
                ...(precoMin && { precoMin }),
                ...(precoMax && { precoMax })
            }

            const response = await anunciosService.listar(filtros)
            setAnuncios(response.anuncios)
            setTotalAnuncios(response.paginacao.total)
        } catch (error) {
            console.error('Erro ao carregar anúncios:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleBuscar = (e) => {
        e.preventDefault()
        carregarAnuncios()
    }

    const limparFiltros = () => {
        setFiltroCategoria('Todas')
        setFiltroRaca('Todas')
        setFiltroEstado('')
        setFiltroCidade('')
        setPrecoMin('')
        setPrecoMax('')
        setOrdenacao('selo')
        setBusca('')
        navigate('/anuncios') // Limpa URL
    }

    // Verificar se pode ver detalhes completos
    const podeVerDetalhes = isLogado && usuario?.perfilCompleto

    const formatPrice = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 0
        }).format(value)
    }

    // Normalização de texto (remove acentos e lowercase)
    const normalizarTexto = (texto) => {
        if (!texto) return ''
        return texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    }

    // Dicionário de Sinônimos (De específico para genérico/categoria)
    const mapaSinonimos = {
        'boi': 'bovino',
        'vaca': 'bovino',
        'bezerro': 'bovino',
        'novilha': 'bovino',
        'terneiro': 'bovino',
        'gado': 'bovino',
        'touro': 'bovino',
        'bode': 'caprino',
        'cabra': 'caprino',
        'cabrito': 'caprino',
        'cavalo': 'equino',
        'egua': 'equino',
        'potro': 'equino',
        'mula': 'equino',
        'burro': 'equino',
        'porco': 'suino',
        'leitao': 'suino',
        'javali': 'suino',
        'ovelha': 'ovino',
        'chibo': 'caprino',
        'carneiro': 'ovino',
        'cordeiro': 'ovino'
    }

    const obterTermosDeBusca = (termo) => {
        const termoNorm = normalizarTexto(termo)
        let termos = [termoNorm]

        // Se o termo for um sinônimo conhecido, adiciona a categoria correspondente
        if (mapaSinonimos[termoNorm]) {
            termos.push(mapaSinonimos[termoNorm])
        }

        // Se o termo for plural (simples), tenta o singular
        if (termoNorm.endsWith('s')) {
            const singular = termoNorm.slice(0, -1)
            if (mapaSinonimos[singular]) {
                termos.push(mapaSinonimos[singular])
            }
            termos.push(singular)
        }

        return termos
    }

    // Filtrar anúncios
    const anunciosFiltrados = anuncios.filter((anuncio) => {
        if (!busca) return true

        const termos = obterTermosDeBusca(busca)

        // Verifica se pelo menos um dos termos corresponde
        return termos.some(termo => {
            // Cria regex para buscar palavra inteira (evita que 'ovino' ache 'bovino')
            // Mas permite busca parcial se a palavra for longa (ex: 'Nel' acha 'Nelore')
            // Para categorias curtas como 'ovino', 'boi', melhor usar boundary ou match exato.

            try {
                const regex = new RegExp(`\\b${termo}`, 'i') // Começa com o termo

                const matchTitulo = regex.test(normalizarTexto(anuncio.titulo))
                const matchCidade = anuncio.cidade && regex.test(normalizarTexto(anuncio.cidade))
                const matchEstado = anuncio.estado && regex.test(normalizarTexto(anuncio.estado))
                const matchRaca = anuncio.raca && regex.test(normalizarTexto(anuncio.raca))

                // Para Categoria, usar igualdade solta (includes) mas cuidado com substrings
                // Se o termo é 'ovino', 'bovino' contém 'ovino'. 
                // Vamos usar a mesma regex de boundary para categoria também.
                const matchCategoria = anuncio.categoria && regex.test(normalizarTexto(anuncio.categoria))

                return matchTitulo || matchCidade || matchEstado || matchRaca || matchCategoria
            } catch (e) {
                // Fallback para includes simples se regex falhar
                const txt = normalizarTexto(termo)
                return normalizarTexto(anuncio.titulo).includes(txt)
            }
        })
    })

    const handleCardClick = (e, anuncioId) => {
        if (isLogado && !usuario?.perfilCompleto) {
            e.preventDefault()
            navigate('/completar-perfil')
        }
    }

    return (
        <div className="anuncios-page">
            <div className="page-header">
                <h1>Anúncios</h1>
                <p>Encontre o gado ideal para sua necessidade</p>
            </div>

            <div className="anuncios-container">
                {/* Sidebar de Filtros */}
                <aside className={`filtros-sidebar ${showFilters ? 'open' : ''}`}>
                    <div className="filtros-header-mobile">
                        <h3>Filtros</h3>
                        <button className="close-filters" onClick={() => setShowFilters(false)}>✕</button>
                    </div>

                    <form className="filtros-form" onSubmit={handleBuscar}>
                        {/* Busca Textual na Sidebar também */}
                        <div className="filtro-group">
                            <label>Busca</label>
                            <input
                                type="text"
                                placeholder="Título, Cidade..."
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                            />
                        </div>

                        <div className="filtro-group">
                            <label>Categoria</label>
                            <select
                                value={filtroCategoria}
                                onChange={(e) => setFiltroCategoria(e.target.value)}
                            >
                                {categorias.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filtro-group">
                            <label>Raça</label>
                            <select
                                value={filtroRaca}
                                onChange={(e) => setFiltroRaca(e.target.value)}
                            >
                                {racas.map(raca => (
                                    <option key={raca} value={raca}>{raca}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filtro-group">
                            <label>Preço (R$)</label>
                            <div className="preco-inputs">
                                <input
                                    type="number"
                                    placeholder="Mín"
                                    value={precoMin}
                                    onChange={(e) => setPrecoMin(e.target.value)}
                                />
                                <input
                                    type="number"
                                    placeholder="Máx"
                                    value={precoMax}
                                    onChange={(e) => setPrecoMax(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="filtro-group">
                            <label>Localização</label>
                            <select
                                value={filtroEstado}
                                onChange={(e) => setFiltroEstado(e.target.value)}
                                className="mb-2"
                            >
                                <option value="">Todos os Estados</option>
                                {estados.map(uf => (
                                    <option key={uf} value={uf}>{uf}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="Cidade"
                                value={filtroCidade}
                                onChange={(e) => setFiltroCidade(e.target.value)}
                            />
                        </div>

                        <div className="filtros-actions">
                            <button type="submit" className="btn btn-primary w-100">Aplicar Filtros</button>
                            <button type="button" className="btn btn-outline w-100" onClick={limparFiltros}>Limpar</button>
                        </div>
                    </form>
                </aside>

                {/* Lista de Anúncios */}
                <main className="anuncios-main">
                    {/* Alerta para completar cadastro */}
                    {isLogado && !usuario?.perfilCompleto && (
                        <div className="alerta-completar">
                            <span>🔒</span>
                            <div>
                                <strong>Perfil Incompleto</strong>
                                <p>Complete seu cadastro para ver preços e detalhes!</p>
                            </div>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={() => navigate('/completar-perfil')}
                            >
                                Completar
                            </button>
                        </div>
                    )}

                    <div className="topo-lista">
                        <button className="btn-filtros-mobile" onClick={() => setShowFilters(true)}>
                            🔍 Filtros
                        </button>
                        <span className="total-resultados">{anunciosFiltrados.length} resultados</span>

                        <div className="ordenacao">
                            <label>Ordenar:</label>
                            <select
                                value={ordenacao}
                                onChange={(e) => setOrdenacao(e.target.value)}
                            >
                                <option value="selo">⭐ Relevância</option>
                                <option value="preco-asc">💰 Menor Preço</option>
                                <option value="preco-desc">💰 Maior Preço</option>
                                <option value="recentes">📅 Mais Recentes</option>
                            </select>
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-container">
                            <div className="spinner"></div>
                            <p>Buscando oportunidades...</p>
                        </div>
                    ) : anunciosFiltrados.length === 0 ? (
                        <div className="sem-resultados">
                            <span className="sem-resultados-icon">🔍</span>
                            <h3>Nenhum anúncio encontrado</h3>
                            <p>Tente ajustar os filtros para encontrar o que procura.</p>
                            <button className="btn btn-outline" onClick={limparFiltros}>Limpar Filtros</button>
                        </div>
                    ) : (
                        <div className="anuncios-grid">
                            {anunciosFiltrados.map((anuncio) => (
                                <AnuncioCard 
                                    key={anuncio.id}
                                    anuncio={anuncio}
                                    podeVerDetalhes={podeVerDetalhes}
                                    isLogado={isLogado}
                                    usuario={usuario}
                                    onClick={handleCardClick}
                                />
                            ))}
                        </div>
                    )}
                </main>
            </div>
        </div>
    )
}

export default Anuncios
